import ChartDataLabels from 'chartjs-plugin-datalabels'

import { LitElement, PropertyValues, html, css } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'

import { Chart } from 'chart.js/auto'
import { format as formatText } from '@operato/utils/format.js'
import DataBinderPlugin from './plugins/chartjs-plugin-data-binder.js'
import SeriesHighlightPlugin from './plugins/chart-series-highlight.js'
import { convertConfigure } from './config-converter.js'

/**
 * Chart.js v4의 CSS transform 좌표 보정 플러그인.
 *
 * v4는 offsetX / chart.width로 마우스 좌표를 계산하는데,
 * offsetX(unscaled)와 chart.width(때에 따라 scaled/unscaled)의
 * 좌표계가 불일치할 수 있다.
 *
 * v2는 (clientX - rect.left) / rect.width로 계산했으므로
 * 둘 다 getBoundingClientRect 기반(= 항상 같은 좌표계)이어서
 * CSS transform과 무관하게 정확했다.
 *
 * 이 플러그인은 beforeEvent에서 v2 방식으로 좌표를 재계산한다.
 */
const V2CoordPlugin = {
  id: 'v2coord',
  beforeEvent(chart: any, args: any) {
    const nativeEvent = args.event.native
    if (!nativeEvent) return

    const canvas = chart.canvas
    const rect = canvas.getBoundingClientRect()
    if (!rect.width || !rect.height) return

    const touches = nativeEvent.touches
    const clientX = touches?.length ? touches[0].clientX : nativeEvent.clientX
    const clientY = touches?.length ? touches[0].clientY : nativeEvent.clientY
    if (clientX === undefined) return

    const dpr = chart.currentDevicePixelRatio

    // v2 공식: clientX와 rect.width 모두 visual(scaled) 좌표계 → 비율 항상 정확
    args.event.x = Math.round((clientX - rect.left) / rect.width * canvas.width / dpr)
    args.event.y = Math.round((clientY - rect.top) / rect.height * canvas.height / dpr)
    args.inChartArea = chart.isPointInArea(args.event)
  }
}

Chart.register(ChartDataLabels, DataBinderPlugin, SeriesHighlightPlugin, V2CoordPlugin)

@customElement('ox-chart')
export class OxChart extends LitElement {
  @property({ type: Number }) declare width: number
  @property({ type: Number }) declare height: number
  @property({ type: Object }) declare options: SceneChart.ChartConfig
  @property({ type: Object }) declare data: SceneChart.ChartData

  private _chart?: SceneChart | null

  @query('canvas') declare _canvas: HTMLCanvasElement

  static styles = css`
    :host {
      display: block;
    }
    canvas {
      width: 100%;
      height: 100%;
    }
  `

  firstUpdated() {
    this.initChart()
  }

  render() {
    return html` <canvas></canvas> `
  }

  dispose() {
    this._chart?.destroy()
    this._chart = null
  }

  updated(changes: PropertyValues<this>) {
    if (changes.has('width') || changes.has('height')) {
      this.updateChartSize()
    }

    if (changes.has('options')) {
      this.updateChartConfig()
    }

    if (changes.has('data') && this._chart) {
      this._chart.data.rawData = this.data
      this._chart.update()
    }
  }

  private _createChart(converted: SceneChart.ChartConfig): SceneChart {
    this.style.width = `${Math.floor(this.width)}px`
    this.style.height = `${Math.floor(this.height)}px`

    const chart = new Chart(this._canvas, {
      type: converted.type as any,
      data: converted.data as any,
      options: converted.options as any
    }) as unknown as SceneChart

    return chart
  }

  initChart() {
    const converted = convertConfigure(this.options as SceneChart.ChartConfig)
    this.attachPluginOptions(converted.options)

    this._chart = this._createChart(converted)
  }

  updateChartSize() {
    this.style.width = `${Math.floor(this.width)}px`
    this.style.height = `${Math.floor(this.height)}px`

    if (!this._chart) return

    this._chart.resize()
  }

  updateChartConfig() {
    if (!this._chart) return

    const converted = convertConfigure(this.options as SceneChart.ChartConfig)
    this.attachPluginOptions(converted.options)

    this._chart.destroy()

    this._chart = this._createChart(converted)
    this._chart.data.rawData = this.data
    this._chart.update()
  }

  attachPluginOptions(options: SceneChart.ChartOptions) {
    if (!options.plugins) {
      options.plugins = {}
    }

    this.attachDatalabelPluginOptions(options.plugins)
  }

  attachDatalabelPluginOptions(pluginOptions: SceneChart.ChartPluginsOptions) {
    pluginOptions.datalabels = {
      ...pluginOptions.datalabels,
      display: function (context: any) {
        const displayValue = context.dataset.displayValue || false
        return displayValue === 'T' ? true : displayValue === 'F' ? false : displayValue
      },
      anchor: function (context: any) {
        return context.dataset.dataLabelAnchor || 'center'
      },
      offset: function (context: any) {
        return context.dataset.dataLabelOffset || 0
      },
      align: function (context: any) {
        return context.dataset.dataLabelAnchor || 'center'
      },
      rotation: function (context: any) {
        return context.dataset.dataLabelRotation || 0
      },
      color: function (context: any) {
        return context.dataset?.defaultFontColor || '#000'
      },
      font: function (context: any) {
        return {
          size: context.dataset?.defaultFontSize,
          family: context.chart.options?.defaultFontFamily
        }
      },
      clamp: true,
      formatter: function (value: any, context: any) {
        const format = context.dataset.valueFormat || ''
        const prefix = context.dataset.valuePrefix || ''
        const suffix = context.dataset.valueSuffix || ''
        if (value == undefined) return value

        const stringValue = format ? formatText(format, Number(value)) : Number(value).toLocaleString()
        return prefix + stringValue + suffix
      }
    }
  }
}
